import { fromEventPattern } from 'rxjs';

import {
    setCustomStatus,
    setCustomReactions,
    disableBackspaceDeleteThread,
    setSidebarDefaultCustomActions,
} from '@/collaboration/comment-element.utils';

let veltModule;
let client;

const veltNotYetImportedError = new Error('Velt client not initialized');

export async function initVelt (apiKey) {
    veltModule = await import('@veltdev/client');
    client = await veltModule.initVelt(apiKey);

    // Velt does not actually _throw an error_ for some reason when it fails to init
    // Catch this via client not being set instead.
    if(!client) {
        throw new Error('Velt failed to initialize');
    }
}

export function setVeltPermissionProvider () {
    if (!client) return;

    client.setPermissionProvider({
        isContextEnabled: true,
        forceRefresh: false,
        retryConfig: {
            retryCount: 3,
            retryDelay: 2000,
        },
        revokeAccessOn: [
            {
                type: 'document_unset',
                revokeOrganizationAccess: false,
            },
            {
                type: 'user_logout',
                revokeOrganizationAccess: false,
            },
        ],
    });
}

export async function identifyUserWithVelt (user, authToken) {
    if (!client) return;

    const resolvedIdentity = await client.identify(user, { authToken });

    if (!resolvedIdentity) {
        throw new Error('Velt client authentication failed');
    }
}

export async function configureVeltAfterIdentified (documentId, context) {
    setCustomStatus(client);
    setCustomReactions(client);

    disableAtHere(client);
    disableBackspaceDeleteThread(client);

    setNotificationMaxDays(client);
    setSidebarDefaultCustomActions(client);

    // Only set document ID if one is explicitly provided
    // Components like dashboards and session replay will set their own with context
    if (documentId) {
        await sendDocumentIdToVelt({ documentId, context });
    }
}

export async function sendDocumentIdToVelt ({ documentId, context }) {
    if (!client) return;

    if (!documentId) {
        await client.unsetDocuments();

        return;
    }

    const documents = [
        {
            id: documentId,
        },
    ];

    const options = context ? { context } : undefined;
    await client.setDocuments(documents, options);
}

export async function getVeltClient () {
    if (!veltModule) {
        return Promise.reject(veltNotYetImportedError);
    }

    return veltModule.getVeltClient();
}

export function signOutWithVelt () {
    if (client) {
        client.signOutUser();
    }
}

export async function setLocationsAfterInitialization (primaryLocation, secondaryLocations = []) {
    let client;

    try {
        client = await getVeltClient();
    } catch (err) {
        if (err === veltNotYetImportedError) {
            setTimeout(() => setLocationsAfterInitialization(primaryLocation, secondaryLocations), 100);
        }

        return;
    }

    client.setLocation(primaryLocation);

    if (!secondaryLocations.length) return;

    secondaryLocations.forEach((location) => {
        client.addLocation(location, true);
    });
}

export function disableAtHere (client) {
    const contactElement = client.getContactElement();
    contactElement.disableAtHere();
}

export function setNotificationMaxDays (client) {
    const notificationElement = client.getNotificationElement();
    notificationElement.setMaxDays(30);
}

/**
 * Creates an RxJS observable from a Velt subscription interface.
 * Automatically unsubscribes when the observable is unsubscribed.
 *
 * @param veltObj The Velt client instance to observe
 *                (e.g. `client.getCommentElement().getAllCommentAnnotations()`)
 */
export function veltToObservable (veltObj) {
    return fromEventPattern(
        (handler) => {
            return veltObj.subscribe(handler);
        },
        (_handler, subscription) => {
            return subscription.unsubscribe();
        },
    );
}

export async function getCommentElement () {
    let client;
    for (let i = 0; i < 5; i++) {
        try {
            // eslint-disable-next-line no-await-in-loop
            client = await getVeltClient();
            break;
        } catch (err) {
            if (i === 4 || err !== veltNotYetImportedError) {
                throw err;
            }

            // eslint-disable-next-line no-await-in-loop
            await new Promise((res) => setTimeout(res, 100));
        }
    }

    return client.getCommentElement();
}
